import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useToast } from '../hooks/useToast';
import {
  ArrowLeft,
  Calendar,
  Download,
  Mail,
  ChevronLeft,
  ChevronRight,
  Check,
  AlertTriangle,
  TrendingUp,
  GraduationCap
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// --- TYPES ---
interface Employee {
  id: string;
  full_name: string;
}

interface DailyStatus {
  status: 'PRESENT' | 'CONGE' | 'MALADIE' | 'FORMATION' | 'ABSENT' | 'WEEKEND';
  label?: string;
  trainingDesc?: string;
}

interface TimeEntryData {
  work_date: string;
  hours_normal: number;
  hours_saturday: number;
  hours_major: number;
  employee_id: { employee_id: string } | null;
}

interface LeaveData {
  employee_id: string;
  start_date: string;
  end_date: string;
  type: string;
}

interface TrainingData {
  employee_id: string;
  day: string;
  description: string;
}

// --- UTILITAIRES ---
const getStartOfWeek = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const newDate = new Date(d.setDate(diff));
  newDate.setHours(0, 0, 0, 0); // Reset heures
  return newDate;
};

// CORRECTION : Gestion du fuseau horaire local
const formatDateDB = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

// Compte les jours ouvrés (Lun-Ven) entre deux dates
const countWeekdays = (startDate: Date, endDate: Date) => {
  let count = 0;
  const curDate = new Date(startDate);
  while (curDate <= endDate) {
    const dayOfWeek = curDate.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) count++;
    curDate.setDate(curDate.getDate() + 1);
  }
  return count;
};

export default function AdminPlanning() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [planning, setPlanning] = useState<Record<string, Record<string, DailyStatus>>>({});
  const [loading, setLoading] = useState(true);
  const [presenceRate, setPresenceRate] = useState(0);

  // Semaine affichée
  const startOfWeek = useMemo(() => getStartOfWeek(currentDate), [currentDate]);
  const weekDays = useMemo(() => Array.from({ length: 7 }).map((_, i) => addDays(startOfWeek, i)), [startOfWeek]);

  // --- CHARGEMENT ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const startStr = formatDateDB(weekDays[0]);
      const endStr = formatDateDB(weekDays[6]);

      // 1. Employés
      const { data: emps } = await supabase.from('employees').select('id, full_name').eq('is_active', true).order('full_name');
      if (emps) setEmployees(emps);

      // 2. Congés Semaine (CORRECTION REQUÊTE)
      // On cherche les congés qui chevauchent la semaine :
      // Début congé <= Fin semaine  ET  Fin congé >= Début semaine
      const { data: leaves } = await supabase
        .from('leaves')
        .select('employee_id, start_date, end_date, type')
        .eq('status', 'validated')
        .lte('start_date', endStr)  // Commence avant ou pendant la semaine
        .gte('end_date', startStr); // Finit après ou pendant la semaine

      // 3. Présences Semaine
      const { data: entries } = await supabase
        .from('time_entries')
        .select('employee_id:weekly_sheets(employee_id), work_date, hours_normal, hours_saturday, hours_major')
        .eq('weekly_sheets.status', 'validated')
        .gte('work_date', startStr)
        .lte('work_date', endStr);

      // 4. Formations depuis training_days
      const { data: trainings } = await supabase
        .from('training_days')
        .select('employee_id, day, description')
        .gte('day', startStr)
        .lte('day', endStr);

      // 5. Construction Matrice
      const matrix: Record<string, Record<string, DailyStatus>> = {};
      const typedLeaves = (leaves as LeaveData[]) || [];
      const typedEntries = (entries as unknown as TimeEntryData[]) || [];
      const typedTrainings = (trainings as TrainingData[]) || [];

      emps?.forEach(emp => {
        matrix[emp.id] = {};
        weekDays.forEach(day => {
          const dStr = formatDateDB(day);
          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
          let status: DailyStatus = { status: isWeekend ? 'WEEKEND' : 'ABSENT' };

          // Vérification formation depuis training_days (priorité haute)
          const training = typedTrainings.find(t => t.employee_id === emp.id && t.day === dStr);
          if (training) {
            status = {
              status: 'FORMATION',
              label: 'FORM',
              trainingDesc: training.description
            };
            matrix[emp.id][dStr] = status;
            return; // On sort, formation a priorité
          }

          // Vérification présence congé
          const leave = typedLeaves.find(l => l.employee_id === emp.id && l.start_date <= dStr && l.end_date >= dStr);
          if (leave) {
            status = {
              status: leave.type === 'formation' ? 'FORMATION' : (leave.type === 'maladie' ? 'MALADIE' : 'CONGE'),
              label: leave.type.toUpperCase().slice(0, 4)
            };
          }

          // Vérification présence travail (écrase l'absence par défaut, mais pas le congé s'il y a un conflit théorique)
          // Note: Si un employé travaille pendant un congé, on affiche le travail ou le congé ?
          // Ici on priorise l'affichage des heures travaillées si elles existent (>0)
          const entry = typedEntries.find(e => e.employee_id?.employee_id === emp.id && e.work_date === dStr);
          const hours = entry ? (entry.hours_normal + entry.hours_saturday + entry.hours_major) : 0;

          if (hours > 0) {
             status = { status: 'PRESENT' };
          }

          matrix[emp.id][dStr] = status;
        });
      });
      setPlanning(matrix);

      // 5. CALCUL STATISTIQUE RÉEL (30 Derniers jours)
      if (emps && emps.length > 0) {
        const today = new Date();
        const thirtyDaysAgo = addDays(today, -30);
        
        const { count: workedDaysCount } = await supabase
          .from('time_entries')
          .select('*', { count: 'exact', head: true })
          .eq('weekly_sheets.status', 'validated')
          .gte('work_date', formatDateDB(thirtyDaysAgo))
          .lte('work_date', formatDateDB(today));

        const businessDays = countWeekdays(thirtyDaysAgo, today);
        const theoreticalMax = emps.length * businessDays;

        if (theoreticalMax > 0 && workedDaysCount !== null) {
          const rate = Math.round((workedDaysCount / theoreticalMax) * 100);
          setPresenceRate(rate > 100 ? 100 : rate);
        } else {
          setPresenceRate(0);
        }
      }

    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [weekDays]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // --- PDF ---
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text(`Planning Hebdomadaire - Semaine ${formatDateDB(startOfWeek)}`, 14, 15);
    const tableColumn = ["Employé", ...weekDays.map(d => d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' }))];
    const tableRows: string[][] = [];

    employees.forEach(emp => {
      const row = [emp.full_name];
      weekDays.forEach(day => {
        const dayStatus = planning[emp.id]?.[formatDateDB(day)];
        const status = dayStatus?.status || '-';
        if (status === 'PRESENT') {
          row.push('X');
        } else if (status === 'WEEKEND') {
          row.push('');
        } else if (status === 'ABSENT') {
          row.push('-');
        } else if (status === 'CONGE') {
          row.push('CP');
        } else if (status === 'FORMATION' && dayStatus?.trainingDesc) {
          row.push(`FORM\n${dayStatus.trainingDesc}`);
        } else if (status === 'FORMATION') {
          row.push('FORM');
        } else if (status === 'MALADIE') {
          row.push('MAL');
        } else {
          row.push(String(status).substring(0, 3));
        }
      });
      tableRows.push(row);
    });

    autoTable(doc, { head: [tableColumn], body: tableRows, startY: 25, styles: { fontSize: 8 }, headStyles: { fillColor: [37, 99, 235] } });
    doc.save(`planning_${formatDateDB(startOfWeek)}.pdf`);
  };

  // --- UI HELPERS ---
  const getStatusUI = (status: DailyStatus) => {
    switch (status.status) {
      case 'PRESENT': return <div style={{ color:'#10b981', display:'flex', justifyContent:'center' }}><Check size={20} strokeWidth={3} /></div>;
      case 'CONGE': return <span style={{ background:'#dbeafe', color:'#1e40af', padding:'4px 8px', borderRadius:'6px', fontSize:'0.75rem', fontWeight:'700' }}>CONGÉ</span>;
      case 'MALADIE': return <span style={{ background:'#fee2e2', color:'#b91c1c', padding:'4px 8px', borderRadius:'6px', fontSize:'0.75rem', fontWeight:'700' }}>MALADIE</span>;
      case 'FORMATION': return (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'2px' }}>
          <span style={{ background:'#f3e8ff', color:'#7e22ce', padding:'4px 8px', borderRadius:'6px', fontSize:'0.75rem', fontWeight:'700', display:'flex', alignItems:'center', gap:'4px' }}>
            <GraduationCap size={14} /> FORM
          </span>
          {status.trainingDesc && (
            <span style={{ fontSize:'0.65rem', color:'#9333ea', maxWidth:'80px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={status.trainingDesc}>
              {status.trainingDesc}
            </span>
          )}
        </div>
      );
      case 'WEEKEND': return <span style={{ color:'#e2e8f0' }}>-</span>;
      default: return <span style={{ color:'#cbd5e1' }}>-</span>;
    }
  };

  const getDailyCount = (dateStr: string) => {
    let count = 0;
    Object.values(planning).forEach(empPlanning => {
      if (empPlanning[dateStr]?.status === 'PRESENT') count++;
    });
    return count;
  };

  const criticalDays = weekDays.filter(day => {
    const dStr = formatDateDB(day);
    const count = getDailyCount(dStr);
    const isWorkDay = day.getDay() !== 0 && day.getDay() !== 6;
    return isWorkDay && count < 3 && employees.length > 0;
  });

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/')} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', cursor: 'pointer' }}>
            <ArrowLeft size={20} color="#64748b" />
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1e293b' }}>Planning Hebdomadaire</h1>
            <p style={{ margin: '4px 0 0', color: '#64748b' }}>Vue d'ensemble des équipes</p>
          </div>
        </div>
        
        <div style={{ display:'flex', gap:'10px' }}>
          <button onClick={handleExportPDF} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'10px 16px', background:'#334155', color:'white', border:'none', borderRadius:'8px', fontWeight:'600', cursor:'pointer' }}>
            <Download size={18} /> PDF
          </button>
          <button onClick={() => toast.info("Envoi email...")} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'10px 16px', background:'#2563eb', color:'white', border:'none', borderRadius:'8px', fontWeight:'600', cursor:'pointer' }}>
            <Mail size={18} /> Email
          </button>
        </div>
      </div>

      {/* ALERTES */}
      {!loading && criticalDays.length > 0 && (
        <div style={{ marginBottom:'24px', background:'#fffbeb', border:'1px solid #fcd34d', borderRadius:'12px', padding:'16px', display:'flex', alignItems:'start', gap:'15px' }}>
          <div style={{ background:'#fbbf24', padding:'8px', borderRadius:'8px', color:'#fff' }}>
            <AlertTriangle size={24} />
          </div>
          <div>
            <h3 style={{ margin:'0 0 5px 0', color:'#92400e', fontSize:'1rem' }}>Tension sur les effectifs détectée</h3>
            <p style={{ margin:0, color:'#b45309', fontSize:'0.9rem' }}>
              Moins de 3 techniciens disponibles les jours suivants : 
              <strong style={{ marginLeft:'5px' }}>
                {criticalDays.map(d => d.toLocaleDateString('fr-FR', { weekday:'long' })).join(', ')}
              </strong>.
            </p>
          </div>
        </div>
      )}

      {/* TABLEAU PLANNING */}
      <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden', marginBottom:'30px' }}>
        
        {/* Contrôles Date */}
        <div style={{ padding:'20px', borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center', background:'#f8fafc' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <Calendar size={20} color="#64748b" />
            <span style={{ fontWeight:'600', color:'#334155' }}>SEMAINE DU {startOfWeek.toLocaleDateString()}</span>
          </div>
          <div style={{ display:'flex', gap:'5px', background:'white', padding:'4px', borderRadius:'8px', border:'1px solid #e2e8f0' }}>
            <button onClick={() => setCurrentDate(addDays(currentDate, -7))} style={{ padding:'6px 12px', border:'none', background:'transparent', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px', fontSize:'0.85rem' }}><ChevronLeft size={16} /> Préc.</button>
            <button onClick={() => setCurrentDate(new Date())} style={{ padding:'6px 12px', border:'none', background:'#eff6ff', color:'#2563eb', borderRadius:'6px', fontWeight:'600', cursor:'pointer', fontSize:'0.85rem' }}>Auj.</button>
            <button onClick={() => setCurrentDate(addDays(currentDate, 7))} style={{ padding:'6px 12px', border:'none', background:'transparent', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px', fontSize:'0.85rem' }}>Suiv. <ChevronRight size={16} /></button>
          </div>
        </div>

        {loading ? <div style={{padding:'40px', textAlign:'center', color:'#64748b'}}>Chargement du planning...</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ padding: '16px', textAlign: 'left', minWidth:'180px', color:'#64748b', fontSize:'0.8rem', borderBottom:'1px solid #e2e8f0', background:'#fff', fontWeight:'600', textTransform:'uppercase' }}>Collaborateur</th>
                  {weekDays.map(day => (
                    <th key={day.toISOString()} style={{ padding: '12px', textAlign: 'center', minWidth:'90px', color:'#64748b', fontSize:'0.85rem', borderBottom:'1px solid #e2e8f0', background: (day.getDay()===0||day.getDay()===6)?'#f8fafc':'#fff' }}>
                      <div style={{ textTransform:'uppercase', fontSize:'0.7rem', marginBottom:'4px' }}>{day.toLocaleDateString('fr-FR', { weekday: 'short' })}</div>
                      <div style={{ fontSize:'1.2rem', fontWeight:'700', color:'#1e293b' }}>{day.getDate()}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.map(emp => (
                  <tr key={emp.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '16px', fontWeight: '600', color: '#334155', background:'#fff' }}>{emp.full_name}</td>
                    {weekDays.map(day => {
                      const dStr = formatDateDB(day);
                      const status = planning[emp.id]?.[dStr] || { status: 'ABSENT' };
                      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                      
                      return (
                        <td key={dStr} style={{ padding: '12px', textAlign: 'center', background: isWeekend ? '#f9fafb' : 'white', borderLeft:'1px solid #f1f5f9' }}>
                          {getStatusUI(status)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                
                {/* LIGNE EFFECTIF */}
                <tr style={{ background:'#f8fafc', borderTop:'2px solid #e2e8f0' }}>
                  <td style={{ padding: '16px', fontWeight: '700', color: '#1e293b', textTransform:'uppercase', fontSize:'0.8rem' }}>Effectif Présent</td>
                  {weekDays.map(day => {
                    const count = getDailyCount(formatDateDB(day));
                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                    return (
                      <td key={day.toISOString()} style={{ padding: '12px', textAlign: 'center', fontWeight:'700', color: count > 0 ? '#10b981' : '#94a3b8', borderLeft:'1px solid #e2e8f0' }}>
                        {isWeekend ? '-' : `${count}/${employees.length}`}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* STATS CARD */}
      <div style={{ maxWidth:'350px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', borderRadius: '16px', padding: '24px', color: 'white', boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.3)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'10px' }}>
          <div style={{ background:'rgba(255,255,255,0.2)', padding:'8px', borderRadius:'8px' }}><TrendingUp size={20} /></div>
          <h3 style={{ margin: 0, fontSize: '0.95rem', opacity: 0.95, fontWeight:'600' }}>Taux de présence moyen</h3>
        </div>
        <div style={{ display:'flex', alignItems:'baseline', gap:'10px' }}>
          <div style={{ fontSize: '3.5rem', fontWeight: '800' }}>{presenceRate}%</div>
          <div style={{ fontSize: '0.9rem', opacity: 0.9, paddingBottom:'10px' }}>sur 30 jours</div>
        </div>
        <div style={{ height:'4px', background:'rgba(255,255,255,0.3)', borderRadius:'2px', marginTop:'5px', overflow:'hidden' }}>
          <div style={{ width: `${presenceRate}%`, background:'white', height:'100%' }}></div>
        </div>
      </div>

    </div>
  );
}